import { IntelligenceModels } from '@models/intelligence.model';
import PlotType = IntelligenceModels.Charts.PlotType;
import Timeframe = IntelligenceModels.Charts.Timeframe;
import XAxisType = IntelligenceModels.Charts.XAxisType;

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
const MAX_TEXT_SEARCH_RANGE_MS = 7 * ONE_DAY_MS;

const ALERT_PLOT_TYPES = new Set<PlotType>([
  PlotType.AlertsByHour,
  PlotType.AlertsByCamera,
  PlotType.AlertTypesDistribution,
]);

const TOTAL_OBJECT_PLOT_TYPES = new Set<PlotType>([
  PlotType.ObjectDetectionsByHour,
  PlotType.ObjectDetectionsByCamera,
]);

const CAMERA_OBJECT_PLOT_TYPES = new Set<PlotType>([
  PlotType.ObjectDetectionsByCamera,
  PlotType.ObjectTypesByCamera,
]);

/** Build the visible Cmd+K follow-up submitted for one actionable chart bar. */
export function buildChartDrilldownPrompt(
  response: IntelligenceModels.Charts.Response,
  selection: IntelligenceModels.Charts.BarSelection,
): string | null {
  const context = resolveSelectionContext(response, selection);
  if (!context) return null;

  const { plotType, cameras, start, end } = context;
  const startIso = new Date(start).toISOString();
  const endIso = new Date(end).toISOString();
  const cameraList = formatCameraList(cameras);

  if (ALERT_PLOT_TYPES.has(plotType)) {
    const alertType =
      plotType === PlotType.AlertTypesDistribution
        ? normalizeVisualQuery(response.data!.labels[selection.labelIndex])
        : null;
    if (plotType === PlotType.AlertTypesDistribution && !alertType) return null;
    return (
      `Use search-alerts to show triggered alerts from ${startIso} to ${endIso} ` +
      `using exactly these chart cameras: ${cameraList}.` +
      (alertType ? ` Filter to alert type "${escapeQuoted(alertType)}".` : '')
    );
  }

  const query = resolveObjectQuery(response, selection, plotType);
  if (!query) return null;
  const prompt =
    `Use smart-search to show matching footage for "${escapeQuoted(query)}" from ${startIso} to ${endIso} ` +
    `using exactly these chart cameras: ${cameraList}. ` +
    'Treat these as representative matching clips, not the exact set counted by the chart.';

  return context.sampled
    ? `${prompt} This samples ${startIso} to ${endIso} because Text Search is limited to a maximum of seven days.`
    : prompt;
}

/** True only when the selected value and every drill-down metadata dependency are valid. */
export function isChartBarActionable(
  response: IntelligenceModels.Charts.Response,
  selection: IntelligenceModels.Charts.BarSelection,
): boolean {
  return buildChartDrilldownPrompt(response, selection) !== null;
}

function resolveSelectionContext(
  response: IntelligenceModels.Charts.Response,
  selection: IntelligenceModels.Charts.BarSelection,
): {
  plotType: PlotType;
  cameras: IntelligenceModels.Charts.AppliedCamera[];
  start: number;
  end: number;
  sampled: boolean;
} | null {
  const { data, appliedFilter, timeRange, plotType, xAxisType } = response;
  const value = data?.series[selection.seriesIndex]?.values[selection.labelIndex];
  if (
    !data ||
    !plotType ||
    value == null ||
    value <= 0 ||
    !appliedFilter?.cameras.length ||
    !isFiniteRange(timeRange)
  ) {
    return null;
  }

  let cameras = appliedFilter.cameras;
  let start = timeRange.start;
  const end = timeRange.end;

  if (xAxisType === XAxisType.Camera) {
    const cameraId = data.rawLabels?.[selection.labelIndex];
    const displayName = data.labels[selection.labelIndex];
    const camera = appliedFilter.cameras.find((item) => item.cameraId === cameraId);
    if (!camera || !displayName) return null;
    cameras = [{ ...camera, name: displayName }];
  } else if (xAxisType === XAxisType.Time) {
    const bucket = resolveTimeBucket(response, selection.labelIndex);
    if (!bucket) return null;
    start = bucket.start;
    return { plotType, cameras, start, end: bucket.end, sampled: false };
  } else if (plotType !== PlotType.AlertTypesDistribution) {
    return null;
  }

  const sampled =
    response.timeframe === Timeframe.LastMonth && CAMERA_OBJECT_PLOT_TYPES.has(plotType);
  if (sampled) start = Math.max(start, end - MAX_TEXT_SEARCH_RANGE_MS);

  return { plotType, cameras, start, end, sampled };
}

function resolveTimeBucket(
  response: IntelligenceModels.Charts.Response,
  labelIndex: number,
): { start: number; end: number } | null {
  const rawLabels = response.data?.rawLabels;
  const chartRange = response.timeRange;
  if (!rawLabels || !isFiniteRange(chartRange)) return null;

  const start = Number(rawLabels[labelIndex]);
  if (!Number.isFinite(start) || start < chartRange.start || start >= chartRange.end) return null;

  const next = Number(rawLabels[labelIndex + 1]);
  const step =
    response.timeframe === Timeframe.LastWeek || response.timeframe === Timeframe.LastMonth
      ? ONE_DAY_MS
      : ONE_HOUR_MS;
  const candidateEnd = Number.isFinite(next) && next > start ? next : start + step;
  const end = Math.min(candidateEnd, chartRange.end);
  return end > start ? { start, end } : null;
}

function resolveObjectQuery(
  response: IntelligenceModels.Charts.Response,
  selection: IntelligenceModels.Charts.BarSelection,
  plotType: PlotType,
): string | null {
  if (TOTAL_OBJECT_PLOT_TYPES.has(plotType)) {
    const objectTypes = response.appliedFilter?.objectTypes;
    return objectTypes?.length ? formatObjectTypes(objectTypes) : 'detected objects';
  }
  return normalizeVisualQuery(response.data?.series[selection.seriesIndex]?.label ?? '');
}

function formatObjectTypes(objectTypes: IntelligenceModels.Charts.ObjectType[]): string {
  if (objectTypes.length === 1) return objectTypes[0];
  if (objectTypes.length === 2) return `${objectTypes[0]} or ${objectTypes[1]}`;
  return `${objectTypes.slice(0, -1).join(', ')}, or ${objectTypes[objectTypes.length - 1]}`;
}

function normalizeVisualQuery(value: string): string | null {
  const normalized = value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return normalized || null;
}

function formatCameraList(cameras: IntelligenceModels.Charts.AppliedCamera[]): string {
  return cameras
    .map((camera) => `${camera.name} [cameraId=${camera.cameraId}, edgeId=${camera.edgeId}]`)
    .join(', ');
}

function escapeQuoted(value: string): string {
  return value.replace(/"/g, '\\"');
}

function isFiniteRange(
  range: IntelligenceModels.Charts.TimeRange | undefined,
): range is Required<Pick<IntelligenceModels.Charts.TimeRange, 'start' | 'end'>> &
  IntelligenceModels.Charts.TimeRange {
  return (
    Number.isFinite(range?.start) &&
    Number.isFinite(range?.end) &&
    (range?.end as number) > (range?.start as number)
  );
}
